import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import {
  PartnerPremiumAccessService,
  PartnerPremiumAccess,
  AssignRequest,
  PartnerPremiumAccessDto,
} from '../../services/partner-premium-access';
import { PartnerService } from '../../services/partner';
import { PremiumRuleService } from '../../services/premium-rule';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

/** Partner option for the dropdowns ({ id, partnerName, partnerCode }). */
interface PartnerOption {
  id: number;
  label: string; // "Acme Agency (PTR-0005)"
}

/** Premium rule option for the multi-select ({ id, premiumCode }). */
interface PremiumOption {
  id: number;
  label: string; // "PR-FAM-26-40 — FAMILY"
}

@Component({
  selector: 'app-partner-premium-access',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    ToggleSwitchModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    IndDatePipe,
  ],
  templateUrl: './partner-premium-access.html',
  styleUrls: ['./partner-premium-access.css'],
})
export class PartnerPremiumAccessPage implements OnInit {
  private svc = inject(PartnerPremiumAccessService);
  private partnerSvc = inject(PartnerService);
  private premiumSvc = inject(PremiumRuleService);
  private toast = inject(ToastService);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  showAssign = signal(false);
  search = signal('');
  rows = signal<PartnerPremiumAccess[]>([]);

  partnerOptions = signal<PartnerOption[]>([]);
  premiumOptions = signal<PremiumOption[]>([]);
  partnerFilter = signal<number | null>(null); // list filter (null = all)

  // Assign dialog model
  assignPartnerId = signal<number | null>(null);
  assignPremiumIds = signal<number[]>([]);
  assignEnable = signal(true);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.partnerName || '').toLowerCase().includes(q) ||
        (r.partnerCode || '').toLowerCase().includes(q) ||
        (r.premiumCode || '').toLowerCase().includes(q) ||
        (r.coverageType || '').toLowerCase().includes(q),
    );
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadPartners();
    this.loadPremiums();
    this.load();
  }

  // ===================== option sources =====================
  private loadPartners() {
    this.partnerSvc.getAll().subscribe((res: any) => {
      if (res?.success === false) {
        this.toast.error(res?.message || 'Failed to load partners');
        return;
      }
      const data = res?.data ?? res ?? [];
      const opts: PartnerOption[] = (Array.isArray(data) ? data : []).map((p: any) => {
        const id = p.id ?? p.Id;
        const name = p.partnerName ?? p.PartnerName ?? `#${id}`;
        const code = p.partnerCode ?? p.PartnerCode;
        return { id, label: code ? `${name} (${code})` : name };
      });
      this.partnerOptions.set(opts);
    });
  }

  private loadPremiums() {
    this.premiumSvc.getAll().subscribe((res: any) => {
      if (res?.success === false) {
        this.toast.error(res?.message || 'Failed to load premium rules');
        return;
      }
      const data = res?.data ?? res ?? [];
      const opts: PremiumOption[] = (Array.isArray(data) ? data : []).map((r: any) => {
        const code = r.premiumCode ?? '';
        const cov = r.coverageType ?? '';
        return { id: r.id, label: cov ? `${code} — ${cov}` : code };
      });
      this.premiumOptions.set(opts);
    });
  }

  // ===================== list =====================
  load() {
    this.loading.set(true);
    const pid = this.partnerFilter();
    const req = pid != null ? this.svc.getByPartner(pid) : this.svc.getAll();
    req.subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  onPartnerFilter(value: any) {
    this.partnerFilter.set(value === '' || value == null ? null : Number(value));
    this.load();
  }

  // ===================== assign =====================
  openAssign() {
    this.assignPartnerId.set(this.partnerFilter() ?? null);
    this.assignPremiumIds.set([]);
    this.assignEnable.set(true);
    this.showAssign.set(true);
    // If a partner is preset, pre-tick what they already have.
    if (this.assignPartnerId() != null) this.prefillAssigned(this.assignPartnerId()!);
  }

  /** Pre-tick the multi-select with the premiums this partner already has. */
  onAssignPartnerChange(partnerId: any) {
    const id = partnerId === '' || partnerId == null ? null : Number(partnerId);
    this.assignPartnerId.set(id);
    this.assignPremiumIds.set([]);
    if (id != null) this.prefillAssigned(id);
  }

  private prefillAssigned(partnerId: number) {
    this.svc.getByPartner(partnerId).subscribe((res: any) => {
      const data = res?.data ?? res ?? [];
      const ids = (Array.isArray(data) ? data : []).map((r: any) => r.premiumID);
      this.assignPremiumIds.set(ids);
    });
  }

  closeAssign() {
    this.showAssign.set(false);
  }

  saveAssign() {
    const partnerId = this.assignPartnerId();
    const premiumIds = this.assignPremiumIds();
    if (partnerId == null) {
      this.toast.warning('Please select a partner');
      return;
    }
    if (!premiumIds.length) {
      this.toast.warning('Please select at least one premium');
      return;
    }

    const body: AssignRequest = {
      partnerID: Number(partnerId),
      premiumIds: premiumIds.map(Number),
      isEnable: this.assignEnable(),
    };

    this.saving.set(true);
    this.svc.assign(body).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to assign premiums');
          return;
        }
        const d = res?.data ?? {};
        const msg =
          res?.message ||
          `${d.assigned ?? 0} premium(s) assigned` +
            (d.skipped ? `, ${d.skipped} already present` : '');
        this.toast.success(msg);
        this.closeAssign();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== row actions =====================
  /** Flip isEnable on a single assignment via Update. */
  toggleEnable(r: PartnerPremiumAccess) {
    const dto: PartnerPremiumAccessDto = {
      id: r.id,
      partnerID: r.partnerID,
      premiumID: r.premiumID,
      isEnable: !r.isEnable,
    };
    this.svc.update(dto).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to update');
          return;
        }
        this.toast.success(dto.isEnable ? 'Assignment enabled' : 'Assignment disabled');
        this.load();
      },
    });
  }

  async remove(r: PartnerPremiumAccess) {
    const ok = await this.toast.confirm(
      `Remove "${r.premiumCode}" from ${r.partnerName}?`,
      'Delete assignment?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Assignment removed');
        this.load();
      },
    });
  }
}
